/**
 * Shared Compare-2-Areas state.
 *
 * Why this exists:
 *   The compare flow was originally map-pin-centric — state lived as a
 *   closure-scoped object inside `main-simple.ts` and only pin taps could
 *   drive it. Users told us they wanted to compare from the places they
 *   were already considering: Top Picks cards and verdict popups. Moving
 *   the state into a module that decision surfaces can subscribe to is the
 *   smallest change that unblocks that.
 *
 * What this does NOT do:
 *   - It does NOT change `showCompareCard` or any facet scoring. The
 *     compare engine (compare-card.ts) is untouched.
 *   - It does NOT decide WHO presents the UI. Entry points (pill, cards,
 *     popup) subscribe, read state, and drive transitions. The state
 *     module only enforces valid transitions + notifies listeners.
 *
 * Mode machine:
 *   off    → enterWaitA()        → waitA
 *   waitA  → selectA(v)          → waitB         (sets a = v)
 *   waitB  → selectB(v, dataset) → off           (opens compare card, clears a)
 *   any    → cancel()            → off
 */

import type { Verdict } from "../data/verdicts";
import { showCompareCard, dismissCompareCard } from "./compare-card";

export type CompareMode = "off" | "waitA" | "waitB";

export interface CompareState {
  mode: CompareMode;
  a: Verdict | null;
}

const listeners = new Set<(s: CompareState) => void>();
let state: CompareState = { mode: "off", a: null };

function notify(): void {
  const snapshot: CompareState = { mode: state.mode, a: state.a };
  for (const fn of Array.from(listeners)) {
    try {
      fn(snapshot);
    } catch {
      /* a broken subscriber must not break the rest */
    }
  }
}

/* -------------------------------------------------------------------------- */
/*  Reads                                                                     */
/* -------------------------------------------------------------------------- */

export function getCompareState(): CompareState {
  return { mode: state.mode, a: state.a };
}

export function getCompareMode(): CompareMode {
  return state.mode;
}

export function getCompareA(): Verdict | null {
  return state.a;
}

/* -------------------------------------------------------------------------- */
/*  Subscription                                                              */
/* -------------------------------------------------------------------------- */

export function subscribeCompare(fn: (s: CompareState) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/* -------------------------------------------------------------------------- */
/*  Transitions                                                               */
/* -------------------------------------------------------------------------- */

/** Enter compare mode. Clears any prior A. Safe to call from any mode. */
export function enterWaitA(): void {
  if (state.mode === "waitA" && state.a === null) return;
  dismissCompareCard();
  state = { mode: "waitA", a: null };
  notify();
}

/**
 * Declare this suburb as Area A. Works from any mode — if we were already
 * in waitB, the old A is replaced and we stay in waitB (user "switched A").
 * If we were off, we jump straight to waitB.
 */
export function selectA(v: Verdict): void {
  state = { mode: "waitB", a: v };
  notify();
}

/**
 * Declare this suburb as Area B. Opens the compare card and returns to
 * `off`. Ignored when A is unset (caller must select A first) or when the
 * provided B is the same as A.
 */
export function selectB(v: Verdict, dataset: readonly Verdict[]): void {
  const a = state.a;
  if (!a) return;
  if (a.id === v.id) return;
  showCompareCard(a, v, {
    dataset: dataset as Verdict[],
    onClose: () => {
      cancel();
    },
  });
  state = { mode: "off", a: null };
  notify();
}

/** Reset to `off`. Use on pill cancel, compare card close, or ESC. */
export function cancel(): void {
  if (state.mode === "off" && state.a === null) return;
  state = { mode: "off", a: null };
  notify();
}

/* -------------------------------------------------------------------------- */
/*  Convenience                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Compose "Compare" action from a decision surface (shortlist card,
 * verdict popup) in one call. Picks the right transition based on current
 * state + whether a dataset is available for finalising B.
 *
 *   - off or waitA   → select v as A (enters waitB)
 *   - waitB, v !== A → finalise with v as B (dataset required)
 *   - waitB, v === A → no-op (this suburb is already A)
 *
 * `dataset` is only needed to finalise a B. Callers that only want to
 * declare an A can pass `null`.
 */
export function compareFromSurface(
  v: Verdict,
  dataset: readonly Verdict[] | null
): void {
  const m = state.mode;
  if (m === "waitB" && state.a && state.a.id !== v.id) {
    if (!dataset) return;
    selectB(v, dataset);
    return;
  }
  selectA(v);
}
